import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import Header from "../../../../components/common/header";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { formatDateToYearMonthDay } from "../../../../utils/date/format-date";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import { SETTLEMENT_CHANGE_NOTICE_URL } from "../../components/settlement-change-modal";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { BsCheckCircleFill } from "react-icons/bs";

const SELLER = gql`
  query seller {
    seller {
      bank
      accountHolder
      accountNumber
      businessNumber
      businessType
      representativeName
      email
      tossPaymentsSellerId
      tossSellerStatus
      settle {
        requestDate
        settleWay
        settlePrice
      }
    }
    calculatePendingSettlement {
      hasRequiredSettlementInfo
      settlePrice
      totalPrice
      settlementStartDate
      settlementEndDate
      nextPayoutDate
    }
    legacyPendingSettlementPreview {
      orderCount
      settlePrice
      cutoffDate
    }
  }
`;
const RESEND_TOSS_VERIFICATION = gql`
  mutation ResendTossVerification {
    resendTossVerification {
      ok
      error
    }
  }
`;

const INITIATE_TOSS_REGISTRATION = gql`
  mutation InitiateTossRegistration {
    initiateTossRegistration {
      ok
      error
      tossSellerStatus
    }
  }
`;

const KAKAO_CHANNEL_URL = "http://pf.kakao.com/_xawxcRn/chat";

const TOSS_STATUS_LABEL: Record<string, { text: string; className: string }> = {
  APPROVAL_REQUIRED: {
    text: "본인인증 대기 중",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  PARTIALLY_APPROVED: {
    text: "본인인증 완료",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  KYC_REQUIRED: {
    text: "추가 서류 확인 필요",
    className: "bg-orange-100 text-orange-800 border-orange-200",
  },
  APPROVED: {
    text: "인증 완료",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
};

const Settlement = () => {
  const {
    data: sellerData,
    loading: queryLoading,
  } = useQuery(SELLER, {
    fetchPolicy: "network-only",
  });
  const [sellerInfoValid, setSellerInfoValid] = useState(false);
  const navigate = useNavigate();

  const [resendTossVerification, { loading: resendLoading }] = useMutation(
    RESEND_TOSS_VERIFICATION
  );

  const [initiateTossRegistration, { loading: initiateLoading }] = useMutation(
    INITIATE_TOSS_REGISTRATION
  );

  const [isVerificationSheetOpen, setIsVerificationSheetOpen] = useState(false);

  // 정산 정보가 모두 입력되었는지 확인 (1페이지 필드 기준)
  useEffect(() => {
    if (sellerData?.seller) {
      const s = sellerData.seller;
      setSellerInfoValid(
        Boolean(s.bank && s.accountHolder && s.accountNumber && s.businessNumber && s.email)
      );
    }
  }, [sellerData]);

  if (queryLoading) return <LoadingSpinnerFull />;

  return (
    <div className="flex flex-col items-center justify-start min-h-screen bg-gray-100 space-y-4 px-4 pb-4">
      <Header
        title="정산센터"
        showBackButton
        onBackClick={() => navigate("/boss/home")}
      />

      {Boolean(sellerData?.legacyPendingSettlementPreview?.orderCount) && (
        <div className="max-w-xl mx-auto w-full rounded-lg shadow-md overflow-hidden border border-amber-200 bg-amber-50">
          <div className="px-5 pt-5 pb-3 space-y-3">
            <p className="text-base font-bold text-amber-800">이전 거래 미정산 내역이 있어요</p>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-base">
                <span className="text-amber-700">미정산 건수</span>
                <span className="font-semibold text-amber-900">
                  {sellerData?.legacyPendingSettlementPreview?.orderCount}건
                </span>
              </div>
              <div className="flex items-center justify-between text-base">
                <span className="text-amber-700">예상 정산금</span>
                <span className="font-semibold text-amber-900">
                  {sellerData?.legacyPendingSettlementPreview?.settlePrice.toLocaleString()}원
                </span>
              </div>
            </div>
            <p className="text-sm text-amber-600">
              {sellerData?.legacyPendingSettlementPreview?.cutoffDate} 이전 거래는 월 자동 정산 대상이 아닙니다. 카카오채널로 문의하시면 확인 후 정산을 도와드립니다.
            </p>
          </div>
          <div className="px-5 pb-5 flex gap-2">
            <button
              type="button"
              onClick={() => window.open(KAKAO_CHANNEL_URL, "_blank")}
              className="flex-1 rounded-xl bg-[#FAE100] py-3 text-base font-semibold text-[#3A1D1D]"
            >
              카카오 문의하기
            </button>
            <button
              type="button"
              onClick={() => window.open(SETTLEMENT_CHANGE_NOTICE_URL, "_blank")}
              className="flex-1 rounded-xl border-2 border-amber-400 text-amber-700 py-3 text-base font-semibold"
            >
              자세히 보기
            </button>
          </div>
        </div>
      )}

      {(() => {
        const hasTossId = Boolean(sellerData?.seller?.tossPaymentsSellerId);
        const status = hasTossId ? sellerData?.seller?.tossSellerStatus : null;
        const statusInfo = status ? TOSS_STATUS_LABEL[status] : null;

        // 미등록 + 정산 정보 완성 → 인증 시작하기 카드
        if (!hasTossId && sellerInfoValid) {
          return (
            <div className="max-w-xl mx-auto w-full p-4 rounded-lg shadow-md border border-blue-200 bg-blue-50">
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 opacity-70 mb-1">
                토스페이먼츠 인증
              </p>
              <p className="text-lg font-bold text-blue-800 mb-3">
                본인인증이 필요해요
              </p>
              <button
                type="button"
                disabled={initiateLoading}
                onClick={() => {
                  initiateTossRegistration()
                    .then((res) => {
                      if (res.data?.initiateTossRegistration?.ok) {
                        setIsVerificationSheetOpen(true);
                      } else {
                        alert(
                          res.data?.initiateTossRegistration?.error ||
                            "인증 시작에 실패했습니다."
                        );
                      }
                    })
                    .catch(() => alert("인증 시작 중 오류가 발생했습니다."));
                }}
                className="w-full rounded-lg bg-blue-500 text-white px-4 py-3 text-base font-semibold disabled:opacity-50"
              >
                {initiateLoading ? "처리 중..." : "인증 시작하기"}
              </button>
            </div>
          );
        }

        // 등록됨 → 상태 카드
        if (!statusInfo) return null;

        const needsResend = status === "APPROVAL_REQUIRED";
        const needsKyc = status === "KYC_REQUIRED";
        const isCompleted =
          status === "PARTIALLY_APPROVED" || status === "APPROVED";

        return (
          <div
            className={clsx(
              "max-w-xl mx-auto w-full p-4 rounded-lg shadow-md border",
              statusInfo.className
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide opacity-70 mb-1">
                  토스페이먼츠 인증
                </p>
                <p className="text-lg font-bold">{statusInfo.text}</p>
                {needsKyc && (
                  <p className="text-sm mt-1 opacity-80">
                    이메일을 확인하고 추가 서류를 제출해 주세요.
                  </p>
                )}
              </div>
              {isCompleted && (
                <BsCheckCircleFill className="shrink-0 ml-4 text-3xl opacity-80" />
              )}
              {needsResend && (
                <button
                  type="button"
                  disabled={resendLoading}
                  onClick={() => {
                    resendTossVerification()
                      .then((res) => {
                        if (res.data?.resendTossVerification?.ok) {
                          alert("인증 문자가 재발송되었어요.");
                        } else {
                          alert(
                            res.data?.resendTossVerification?.error ||
                              "재발송에 실패했습니다."
                          );
                        }
                      })
                      .catch(() => alert("재발송 중 오류가 발생했습니다."));
                  }}
                  className="ml-4 shrink-0 rounded-lg bg-white bg-opacity-60 border border-current px-3 py-2 text-base font-semibold disabled:opacity-50"
                >
                  {resendLoading ? "발송 중..." : "문자 재발송"}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      <div className="max-w-xl mx-auto bg-white w-full rounded-lg shadow-md overflow-hidden">
        {(() => {
          const s = sellerData?.seller;
          const hasAnyInfo = Boolean(s?.bank || s?.accountNumber || s?.businessNumber);

          // 미등록 상태
          if (!hasAnyInfo) {
            return (
              <div className="p-5">
                <h2 className="text-xl font-bold mb-1">정산 정보</h2>
                <p className="text-base text-gray-500 mb-4">
                  계좌 정보를 등록하면 매달 자동으로 정산받을 수 있어요.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/boss/my/banking/settlement-info-update")}
                  className="w-full bg-blue-500 text-white font-semibold py-3 rounded-xl"
                >
                  정산 정보 등록하기
                </button>
              </div>
            );
          }

          // 등록된 정보 표시
          const businessTypeLabel =
            s?.businessType === "CORPORATE"
              ? "법인"
              : s?.businessType === "INDIVIDUAL_BUSINESS"
                ? "개인"
                : null;
          const formatBusinessNumber = (num: string) => {
            const digits = num.replace(/\D/g, "");
            if (digits.length === 10) {
              return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
            }
            return num;
          };
          const businessNumberDisplay = s?.businessNumber
            ? businessTypeLabel
              ? `${formatBusinessNumber(s.businessNumber)} (${businessTypeLabel})`
              : formatBusinessNumber(s.businessNumber)
            : null;

          const fields: { label: string; value: string | null | undefined; required: boolean }[] = [
            { label: "은행", value: s?.bank, required: true },
            { label: "계좌번호", value: s?.accountNumber, required: true },
            { label: "예금주", value: s?.accountHolder, required: true },
            { label: "사업자번호", value: businessNumberDisplay, required: true },
            { label: "이메일", value: s?.email, required: true },
          ];
          const missingFields = fields.filter((f) => f.required && !f.value);

          return (
            <>
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <h2 className="text-xl font-bold">정산 정보</h2>
                <button
                  type="button"
                  onClick={() => navigate("/boss/my/banking/settlement-info-update")}
                  className="text-base text-blue-500 font-semibold"
                >
                  수정하기
                </button>
              </div>

              {missingFields.length > 0 && (
                <div className="mx-5 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 flex items-start gap-2">
                  <span className="text-amber-500 text-base mt-0.5">⚠️</span>
                  <p className="text-base text-amber-700">
                    <span className="font-semibold">
                      {missingFields.map((f) => f.label).join(", ")}
                    </span>
                    {" "}이(가) 없어 정산이 지연될 수 있어요.
                  </p>
                </div>
              )}

              <div className="divide-y divide-gray-100 px-5 pb-5">
                {fields.map((field) => (
                  <div key={field.label} className="flex items-center justify-between py-2.5">
                    <span className="text-base text-gray-500 w-20 shrink-0">{field.label}</span>
                    {field.value ? (
                      <span className="text-base font-medium text-gray-900 text-right truncate ml-2">
                        {field.value}
                      </span>
                    ) : (
                      <span className="text-base font-semibold text-amber-500">미입력</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      <BottomSheet
        isOpen={isVerificationSheetOpen}
        onClose={() => setIsVerificationSheetOpen(false)}
        eyebrow="최초 1회 인증이 필요해요"
        mainText="📩 인증 문자가 발송됐어요"
        subText={`등록된 번호로 토스페이먼츠 인증 문자를 보내드렸어요.\n문자의 링크를 눌러 인증을 완료해 주세요.`}
        buttonText="확인"
        onButtonClick={() => setIsVerificationSheetOpen(false)}
        disableDrag
      />

      {/* 정산 금액 카드 */}
      <div className="max-w-xl mx-auto bg-white w-full rounded-lg shadow-md overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-xl font-bold mb-4">정산 금액</h2>

          {sellerData?.calculatePendingSettlement ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-base text-gray-500">정산 예정 금액</span>
                <span className="text-3xl font-bold text-gray-900">
                  {sellerData.calculatePendingSettlement.settlePrice.toLocaleString()}원
                </span>
              </div>
              <div className="flex items-center justify-between text-base">
                <span className="text-gray-500">집계 기간</span>
                <span className="text-gray-700">
                  {formatDateToYearMonthDay(
                    new Date(sellerData.calculatePendingSettlement.settlementStartDate)
                  )}
                  {" ~ "}
                  {formatDateToYearMonthDay(
                    new Date(sellerData.calculatePendingSettlement.settlementEndDate)
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-base">
                <span className="text-gray-500">다음 지급 예정일</span>
                <span className="font-semibold text-blue-600">
                  {formatDateToYearMonthDay(
                    new Date(sellerData.calculatePendingSettlement.nextPayoutDate)
                  )}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-base text-gray-500">정산 정보를 먼저 입력해주세요.</p>
          )}
        </div>

        <div className="px-5 pb-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate("/boss/my/banking/settlement-detail")}
            className="w-full bg-blue-500 text-white font-semibold py-3 rounded-xl mt-2"
          >
            정산 내역 보기
          </button>
          <button
            type="button"
            onClick={() => navigate("/boss/my/banking/settlement-paid")}
            className="w-full border-2 border-green-500 text-green-600 font-semibold py-3 rounded-xl"
          >
            정산 완료 내역 보기
          </button>
        </div>
      </div>

      {/* 자동 정산 안내 카드 */}
      <div className="max-w-xl mx-auto bg-white w-full rounded-lg shadow-md overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-xl font-bold mb-4">자동 정산 안내</h2>

          <div className="space-y-4">
            {[
              {
                title: "정산 방식",
                items: [
                  "전월 거래내역 기준으로 월 1회 자동 정산됩니다",
                  "별도의 정산 요청이나 현금영수증 발행은 필요하지 않습니다",
                ],
              },
              {
                title: "지급 시점",
                items: [
                  "정산금은 매달 영업일 기준 첫날 자동 지급됩니다",
                  "정확한 지급을 위해 계좌와 사업자 정보를 최신 상태로 유지해주세요",
                ],
              },
              {
                title: "세무 처리",
                items: [
                  "거래내역은 부가세 신고 참고자료로 활용하실 수 있습니다",
                  "마감히어로는 플랫폼 수수료에 대한 세금계산서를 발행해드립니다",
                ],
              },
              {
                title: "확인 방법",
                items: [
                  "정산 상세내역에서 월별 거래와 정산 상태를 확인할 수 있습니다",
                  "레거시 미정산 건이 있다면 고객센터로 문의해주세요",
                ],
              },
            ].map((section, i, arr) => (
              <div key={section.title} className={clsx(i < arr.length - 1 && "border-b border-gray-100 pb-4")}>
                <p className="text-base font-bold text-gray-800 mb-1.5">{i + 1}. {section.title}</p>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item} className="text-base text-gray-500 flex gap-1.5">
                      <span className="shrink-0 text-gray-300">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {!sellerData?.calculatePendingSettlement?.hasRequiredSettlementInfo && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 flex items-start gap-2">
              <span className="text-amber-500 text-sm mt-0.5">⚠️</span>
              <p className="text-base text-amber-700">자동 정산을 위해 정산 정보를 먼저 등록해주세요</p>
            </div>
          )}
        </div>

        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={() => navigate("/boss/my/banking/settlement-request")}
            className="w-full border-2 border-blue-500 text-blue-500 font-semibold py-3 rounded-xl"
          >
            자동 정산 안내 보기
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settlement;
